import { Button, Chip, FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import { padding } from "@mui/system";
import { useEffect, useState, useCallback } from "react";
import { MdEdit } from "react-icons/md";
import { IoSaveOutline } from "react-icons/io5";
import { ApiRequest } from "GlobalFunctions";
import { set } from "lodash";
import { useSnackbar, withSnackbar } from "notistack";
import { Link, useSearchParams } from "react-router-dom";
import PaymentMethodForm from "./Components/PaymentMethodForm";
import Payment from "../Payment/Payment";
import AlertDialog from "elements/AlertDialog";
import ImageUploader from "elements/ImageUploader";
import CreditCard from "./Components/CreditCard";
import Email from "./Components/Email";
import AutocompleteField from "elements/AutocompleteField";
import { ProjectConfig } from "Global";
import StoreLocations from "../StoreLocations/StoreLocations";
import BillingHistory from "./Components/BillingHistory";
import EventLogs from "elements/EventLogs";
import TransferStoreSection from "./Components/TransferStoreSection";
import StoreTransfer from "../StoreTransfer/StoreTransfer";
import PasswordChange from "./Components/PasswordChange";
import EmailChange from "./Components/EmailChange";


export default function AccountEditor(props:any) {
    /*SNACKBAR FUNCTION FOR NOTIFICATIONS*/
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    /*============================================= */

    /*URL SEARCH PARAMS TO CAPTURE TRANSFER ID*/
    const [searchParams] = useSearchParams();
    const transferId = searchParams.get('tid');
    const isTransferRequest = !!transferId;
    /*============================================= */

    const {setLoading, GetUserInfo} = props;
    const [accountValues, setAccountValues] = useState<any>({
        company_name: '',
        contact_person_name: '',
        contact_person_lastname: '',
        email: '',
        company_id: '',
        store_id: '',
        country_id: '1',
        currency: 'USD',
        tax_mode: 'origin',
        logo: '',
        payment_data:{},
        region_id: '',
        city_id: '',
        is_transfer_pending: false
    });

    const [activeAccountEditor, setActiveAccountEditor] = useState(false);
    const [hasConnectedStore, setHasConnectedStore] = useState(false);
    const [isEditingPassword, setIsEditingPassword] = useState(false);
    const [isEditingEmail, setIsEditingEmail] = useState(false);
    

    useEffect (() => {
        console.log("Checking for connected store...");
        // Check if store_id exists in localStorage
        const storeId = localStorage.getItem('store_id');
        if (storeId) {
            setHasConnectedStore(true);
            getAccountDetails();
        } else {
            setHasConnectedStore(false);
        }
        setLoading(false);
    }, []);


    const handleInputChange = (e: { target: { name: any; value: any; }; }) => {
        const { name, value } = e.target;
        setAccountValues({
          ...accountValues,
          [name]: value,
        });
      };

    
    /*=============================================
    GET ACCOUNT DETAILS
    =============================================*/
    const getAccountDetails = async () => {

        await getCountries();
        await ApiRequest({
            method: 'GET',
            url: '/stores/getStoreAccount',
            setResponse: (response: any) => {
                setAccountValues(response.data);
            }
        });
    }

    const [open, setOpen] = useState(false);
    const [authPopup, setAuthPopup] = useState<Window | null>(null);

    // Handle messages from the popup window
    const handleAuthMessage = useCallback((event: MessageEvent) => {
        const data = event.data;
        
        if (data && data.status === 'success' && data.ltkn) {
            // Store the auth token
            localStorage.setItem('store_id', data.store_id);
            
            // Notify user
            enqueueSnackbar('Store added successfully! Redirecting...', { 
                variant: 'success',
                autoHideDuration: 3000
            });
            
            // Reload page
            setTimeout(() => {
                window.location.reload();
            }, 2000);
        } else if (data && data.status === 'error') {
            enqueueSnackbar(data.message || 'Authentication failed', { 
                variant: 'error',
                autoHideDuration: 5000
            });
        }
    }, [enqueueSnackbar]);

    // Setup and cleanup the message listener
    useEffect(() => {
        window.addEventListener('message', handleAuthMessage);
        
        return () => {
            window.removeEventListener('message', handleAuthMessage);
            
            // Close the popup if it's still open when component unmounts
            if (authPopup && !authPopup.closed) {
                authPopup.close();
            }
        };
    }, [handleAuthMessage, authPopup]);

    const handleNewStore = () => {
        setOpen(false);
        openWebflowModal();
    }

    const handleReconnectWebflow = () => {
        openWebflowModal();
    }



    //   const [storeData, setStoreData] = useState<any>({});
    
    //   useEffect(() => {
    //     getStoreInfo();
    //   }, []);
    
    //   const getStoreInfo = async () => {
    //     const userInfo = await ApiRequest({
    //         url: '/stores/getStoreInfo',
    //         method: 'GET',
    //         setResponse: (response: any) => {
    //           console.log("STORE INFO", response);
    //           setStoreData(response.data);
              
    //         }
    //     });
    //   }


    const openWebflowModal = async () => {
            // Get the authentication token
            const ltkn = localStorage.getItem('ltkn');
            if (!ltkn) {
                enqueueSnackbar('Authentication error, please log in again', { 
                    variant: 'error',
                    autoHideDuration: 3000
                });
                return;
            }
            
            let url = `https://webflow.com/oauth/authorize?response_type=code`
            url += `&client_id=${ProjectConfig.WEBFLOW_CLIENT_ID}`
            url += `&redirect_uri=`
            url += `&state=addsite-${ltkn}` // Send token as part of state
            url += `&scope=assets%3Aread%20assets%3Awrite%20authorized_user%3Aread%20cms%3Aread%20cms%3Awrite%20custom_code%3Aread%20custom_code%3Awrite%20forms%3Aread%20forms%3Awrite%20pages%3Aread%20pages%3Awrite%20sites%3Aread%20sites%3Awrite%20ecommerce%3Aread%20ecommerce%3Awrite%20users%3Aread%20users%3Awrite`;
        
            const popup = window.open(url, '_blank', 'location=yes,scrollbars=yes,status=yes');
            
            setAuthPopup(popup);
        
            if (popup) {
                const popupCheckInterval = setInterval(() => {
                    if (popup.closed) {
                        clearInterval(popupCheckInterval);
                        setAuthPopup(null);
                    }
                }, 500);
            }
        };


    /*=============================================
    UPDATE ACCOUNT
    =============================================*/
    const updateAccount = () => {

        // Validate required fields
        if (!accountValues.country_id || !accountValues.region_id || !accountValues.city_id) {
            enqueueSnackbar('Please fill in all location fields', { variant: 'error' });
            return;
        }

        const body = new FormData();
        body.append('formValues', JSON.stringify(accountValues));

        if (files) {
            body.append("logo", files);
        }

        ApiRequest({
            method: 'POST',
            url: '/stores/updateStoreAccount',
            formData: true,
            body: body,
            setResponse: (response: any) => {
                enqueueSnackbar('Account updated', { variant: 'success' });
                setActiveAccountEditor(false);
                GetUserInfo();
            },
        });
    }

    /*=============================================
    GET COUNTRIES
    =============================================*/
    const [countries, setCountries] = useState([]);
    const getCountries = async () => {
        await ApiRequest({
            url: '/countries/getCountries',
            method: 'GET',
            setResponse: (response: any) => {
                setCountries(response);
            }
        });
    }

    const validateEmail = (email: string) => {
        const re = /\S+@\S+\.\S+/;
        return re.test(email);
    }



    const [openDeleteAlert, setOpenDeleteAlert] = useState(false);

    const deleteStore = async () => {
        setLoading(true);
        await ApiRequest({
            method: 'POST',
            url: '/stores/deleteStore',
            setResponse: (response: any) => {
                if(response.default_store_id) {
                    localStorage.setItem('store_id', response.default_store_id);
                } 
                else {
                    localStorage.removeItem('store_id');
                }
                enqueueSnackbar('Store deleted', { variant: 'success' });
                window.location.href = '/';
            },
        });
        
    }
    
    /*=============================================
    CANCEL STORE TRANSFER
    =============================================*/
    const cancelTransfer = async () => {
        await ApiRequest({
            method: 'POST',
            url: '/stores/cancelTransfer',
            setResponse: (response: any) => {
                if (response.status === "success") {
                    enqueueSnackbar('Store transfer cancelled', { variant: 'success' });
                    window.location.reload();
                } else {
                    enqueueSnackbar('Failed to cancel store transfer', { variant: 'error' });
                }
            },
        });
    }

    const [files, setFiles] = useState<any>([]);

    // If there's a transfer request, always show the StoreTransfer component
    if (isTransferRequest) {
        return (
            <div className='ModuleContainer'>
                <div className="ModuleHeader">
                    <h1>Account</h1>
                </div>
                <div className='ModuleContent'>
                    <StoreTransfer />
                </div>
            </div>
        );
    }

    // If no store is connected, show the no-store message
    if (!hasConnectedStore) {
        return (
            <div className='ModuleContainer'>
                <div className="ModuleHeader">
                        <h1>Account</h1>
                </div>
                <div className='ModuleContent' style={{ textAlign: 'center', padding: '60px 0px' }}>
                    <div style={{ backgroundColor: '#F9F9FC', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px', borderRadius: '8px', gap: '30px' }}>   
                        <span style={{ color: '#666', fontSize: '16px' }}>
                            You have no connected stores, please connect a Webflow project to re-enable your account
                        </span>
                        <Button
                            variant="contained"
                            onClick={handleReconnectWebflow}
                            color="primary"
                            size="small"
                            sx={{
                                height: '36px',
                            }}
                        >
                            Reconnect Webflow Account
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    // If editing password, show only the password change component
    if (isEditingPassword) {
        return (
            <PasswordChange 
                setIsEditingPassword={setIsEditingPassword} 
                setActiveAccountEditor={setActiveAccountEditor}
            />
        );
    }

    // If editing email, show only the email change component
    if (isEditingEmail) {
        return (
            <EmailChange 
                setIsEditingEmail={setIsEditingEmail} 
                setActiveAccountEditor={setActiveAccountEditor}
                currentEmail={accountValues.email}
            />
        );
    }

    return (
        <>
        <div className='ModuleContainer'>
            <div className="ModuleHeader">
                <div className="ComponentTitle">
                    <div className="account_title_row">
                        <h1>Account</h1>
                        {accountValues.is_transfer_pending && (
                            <>
                                <Chip 
                                    label={
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '14px'  }}>
                                            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <circle cx="5" cy="5" r="4.5" stroke="#424242" strokeDasharray="0.8 0.8"/>
                                            </svg> 
                                            Pending Transfer
                                        </span>
                                    } 
                                    style={{ backgroundColor: "#F3F3F3", color: "#424242", borderRadius: "5px", marginBottom: '6px' }}
                                />

                                {/* <a>Cancel Transfer</a> */}
                                <Button
                                    variant="text"
                                    onClick={() => {
                                        cancelTransfer();
                                    }}
                                    sx={{
                                        textTransform: 'none',
                                        color: '#424242',
                                        fontSize: '14px',
                                        borderRadius: '5px !important',
                                        height: '32px',
                                        marginBottom: '6px',
                                        paddingLeft: '8px',
                                        paddingRight: '8px',
                                    }}
                                    size="small"
                                >
                                    Cancel Transfer
                                </Button>
                            </>
                        )}
                    </div>
                </div>
                <div className="ModuleActions">

                </div>
            </div>

            <div id='account_editor' className='ModuleContent'>
                
                <div className="account_section">
                    <div className="section_header">
                        <h2>Profile</h2>
                        <div className="section_actions">

                            {activeAccountEditor && (
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        setActiveAccountEditor(false);
                                    }}
                                    color="info"
                                    className="btnList2"
                                >
                                    Cancel
                                </Button>
                            )}
                            
                            
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    !activeAccountEditor? setActiveAccountEditor(true) :
                                    updateAccount();
                                }}
                                color="info"
                                className="btnList2"
                                startIcon={activeAccountEditor ? <IoSaveOutline /> : <MdEdit />}
                            >
                                {activeAccountEditor ? 'Save' : 'Edit'}
                            </Button>
                        </div>
                    </div>

                    <div className="account_row">
                        <span className="account_label">Logo</span>
                            <ImageUploader 
                                className="account_logo"
                                image={accountValues.logo} 
                                setImage={(newImage) => setAccountValues({ ...accountValues, logo: newImage })}
                                setFiles={setFiles} 
                                disabled={!activeAccountEditor}
                            />
                    </div>

                    <div className="account_row">
                        <span className="account_label">Company Name</span>
                        <TextField
                            name="company_name"
                            value={accountValues.company_name}
                            fullWidth
                            size="small"
                            inputProps={{ readOnly: !activeAccountEditor }}
                            className={!activeAccountEditor ? 'input_editor input_readonly' : 'input_editor'}

                            onChange={handleInputChange}
                        />
                    </div>
                    <div className="account_row">
                        <span className="account_label">Contact person</span>
                        <div className="input_row">
                            <TextField
                                name="contact_person_name"
                                value={
                                    !activeAccountEditor? accountValues.contact_person_name + ' ' + accountValues.contact_person_lastname :
                                    accountValues.contact_person_name
                                }
                                fullWidth
                                size="small"
                                inputProps={{ readOnly: !activeAccountEditor }}
                                className={!activeAccountEditor ? 'input_editor input_readonly' : 'input_editor'}
                                onChange={handleInputChange}
                            />
                            
                            {activeAccountEditor && (
                                <TextField
                                    name="contact_person_lastname"
                                    value={accountValues.contact_person_lastname}
                                    fullWidth
                                    size="small"
                                    inputProps={{ readOnly: !activeAccountEditor }}
                                    className={!activeAccountEditor ? 'input_editor input_readonly' : 'input_editor'}
                                    onChange={handleInputChange}
                                />
                            )}
                        </div>
                    </div>
                    <div className="account_row">
                        <span className="account_label">Email</span>
                        <TextField
                            name="email"
                            value={accountValues.email}
                            fullWidth
                            size="small"
                            inputProps={{ readOnly: true }}
                            className={!activeAccountEditor ? 'input_editor input_readonly' : 'input_editor'}
                            InputProps={{
                                endAdornment: activeAccountEditor && (
                                    <Button
                                        variant="text"
                                        onClick={() => setIsEditingEmail(true)}
                                        sx={{
                                            textTransform: 'none',
                                            color: '#7963FF !important',
                                            fontSize: '12px !important',
                                            minWidth: 'auto',
                                            padding: '2px 6px',
                                            height: '2px',
                                            whiteSpace: 'nowrap',
                                        }}
                                        size="small"
                                    >
                                        Edit email
                                    </Button>
                                )
                            }}
                        />
                    </div>
                    <div className="account_row">
                        <span className="account_label">Password</span>
                        <TextField
                            name="password"
                            value="*********"
                            fullWidth
                            size="small"
                            type='password'
                            inputProps={{ readOnly: !activeAccountEditor }}
                            className={!activeAccountEditor ? 'input_editor input_readonly' : 'input_editor'}
                            InputProps={{
                                endAdornment: activeAccountEditor && (
                                    <Button
                                        variant="text"
                                        onClick={() => setIsEditingPassword(true)}
                                        sx={{
                                            textTransform: 'none',
                                            color: '#7963FF !important',
                                            fontSize: '12px !important',
                                            minWidth: 'auto',
                                            padding: '2px 6px',
                                            height: '2px',
                                            whiteSpace: 'nowrap',
                                        }}
                                        size="small"
                                    >
                                        Edit password
                                    </Button>
                                )
                            }}
                        />
                    </div>
                    <div className="account_row">
                        <span className="account_label">Company #</span>
                        <TextField
                            name="company_id"
                            value={accountValues.company_id}
                            fullWidth
                            size="small"
                            inputProps={{ readOnly: !activeAccountEditor }}
                            className={!activeAccountEditor ? 'input_editor input_readonly' : 'input_editor'}

                            onChange={handleInputChange}
                        />
                    </div>
                    <div className="account_row">
                        <span className="account_label">Country</span>
                        <FormControl fullWidth>
                            <Select
                                id="country_select"
                                size="small"
                                value={accountValues.country_id}
                                name="country"
                                inputProps={{ readOnly: !activeAccountEditor }}
                                className={!activeAccountEditor ? 'input_editor input_readonly' : 'input_editor'}
                                onChange={(e) => {
                                    setAccountValues({
                                        ...accountValues, 
                                        country_id: e.target.value,
                                        region_id: '',
                                        city_id: ''
                                    })
                                }}
                            >
                                {countries.map((country: any, index: number) => {
                                    return (
                                        <MenuItem key={index} value={country.country_id}>{country.country_name}</MenuItem>
                                    ) 
                                })}
                                
                            </Select>
                        </FormControl>
                    </div>

                    {accountValues.country_id !== '' && (
                        <div className="account_row">
                            <span className="account_label">State</span>
                            <AutocompleteField
                                endpoint="/countries/getRegions"
                                endpoint_params={{ country_id: accountValues.country_id }}
                                value={accountValues.region_id}
                                disabled={!activeAccountEditor}
                                size="small"
                                readOnly = {!activeAccountEditor}
                                className={!activeAccountEditor ? 'input_editor input_readonly' : 'input_editor'}
                                onChange={(data: any) => {
                                    setAccountValues({
                                        ...accountValues, 
                                        region_id: data ? data.value : '',
                                        city_id: ''
                                    })
                                }}
                                fullWidth
                            />
                        </div>
                    )}

                    {accountValues.region_id !== '' && (
                        <div className="account_row">
                            <span className="account_label">City</span>
                            <AutocompleteField
                                endpoint="/shipping/zones/getCities"
                                endpoint_params={{ region_id: accountValues.region_id }}
                                max_results={10}
                                size="small"
                                readOnly = {!activeAccountEditor}
                                className={!activeAccountEditor ? 'input_editor input_readonly' : 'input_editor'}
                                dynamic={true}
                                value={accountValues.city_id}
                                disabled={!activeAccountEditor}
                                onChange={(value: any) => {
                                    setAccountValues({
                                        ...accountValues, 
                                        city_id: value ? value.value : ''
                                    })
                                }}
                                clearOnSelect={true}
                                fullWidth
                            />
                        </div>
                    )}

                    <div className="account_row">
                        <span className="account_label">Currency</span>
                        <FormControl fullWidth>
                            <Select
                                id="currency_select"
                                size="small"
                                value={accountValues.currency}
                                name="currency"
                                inputProps={{ readOnly: !activeAccountEditor }}
                                className={!activeAccountEditor ? 'input_editor input_readonly' : 'input_editor'}
                                onChange={(e) => {
                                    setAccountValues({...accountValues, currency: e.target.value })
                                }}
                            >
                                <MenuItem value="USD">USD</MenuItem>
                                <MenuItem value="CAD">CAD</MenuItem>
                            </Select>
                        </FormControl>
                    </div>

                    <div className="account_row">
                        <span className="account_label">Tax Configuration</span>
                        <FormControl fullWidth>
                            <Select
                                id="tax_mode_select"
                                size="small"
                                value={accountValues.tax_mode}
                                name="tax_mode"
                                inputProps={{ readOnly: !activeAccountEditor }}
                                className={!activeAccountEditor ? 'input_editor input_readonly' : 'input_editor'}
                                onChange={(e) => {
                                    setAccountValues({...accountValues, tax_mode: e.target.value })
                                }}
                            >
                                <MenuItem value="origin">Origin</MenuItem>
                                <MenuItem value="destination">Destination</MenuItem>
                            </Select>
                        </FormControl>
                    </div>
                </div>
                
                <div className="account_section">
                    <div className="section_header">
                        <h2>Connect Webflow Website</h2>
                        <div className="section_actions">
                        </div>
                    </div>

                    <span className="account_description_text">Add a new Webflow project from the drop down in the top right</span>

                    <div className="account_row">
                        <span className="account_label">Webflow account linked</span>
                        <Button
                            variant="contained"
                            onClick={handleNewStore}
                        >
                            Reconnect Webflow Account
                        </Button>
                    </div>
                </div>


                 {/* Plan */}
                 <div className="account_section">
                    <div className="section_header">
                        <h2>Plan</h2>
                        <div className="section_actions">
                        </div>
                    </div>
                    <Payment setLoading={setLoading} style="account" />
                </div>

                <CreditCard payment_data={accountValues.payment_data} />

                <BillingHistory />
                
                <div className="account_section">
                    <div className="section_header">
                        <h2>User Logs</h2>
                    </div>
                    <div className="billing_history_table">
                        <div id="user_logs">
                            <EventLogs 
                                url="/stores/userLogs" 
                                queryParams={{ store_id: localStorage.getItem("store_id") }}
                            />
                        </div>
                    </div>
                </div>


                <div className="account_section">
                    <div className="section_header">
                        <h2>Manage User Access</h2>
                        <div className="section_actions">

                        </div>
                    </div>
                    <TransferStoreSection />
                </div>




                {/* PAYMENTS */}
                <div className="account_section">
                    <div className="section_header">
                        <h2>Payment Gateway</h2>
                        <div className="section_actions">
                        </div>
                    </div>
                        <PaymentMethodForm />
                </div>

                {/* EMAIL */}
                <Email />

                {/* STORE LOCATIONS */}
                <StoreLocations />

                {/* PLAN ACTIONS */}
                <div className="account_section">
                    <div className="section_header">
                        <h2>Plan Actions</h2>
                        <div className="section_actions">
                        </div>
                    </div>
                    <div className="account_row">
                        <span className="account_label">Delete</span>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={() => setOpenDeleteAlert(true)}
                            sx={{
                                backgroundColor: '#BD2B2B',
                                '&:hover': {
                                    backgroundColor: '#A02525'
                                }
                            }}
                        >
                            Delete Store
                        </Button>
                    </div>
                </div>

                
                
            </div>
        </div>

        <AlertDialog
            open={openDeleteAlert}
            setOpen={setOpenDeleteAlert}
            send={deleteStore}
            input_confirm_required={true}
            input_confirm_required_text="Delete Store"
            input_confirm_required_label="Type 'Delete Store' to confirm"
            button_color="error"
            accept_text="Delete"
            cancel_text="No, Don't Delete"
        />
        </>
    )
}